import React, { FC, Fragment, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { api, AssetLocation, AssetLocationTypes, AssetType, KeyManagementTypes } from "../../logic/api";
import DashPage from "../dash/DashPage";
import styles from "./styles";
import classes from "./styles.module.css";
import { FiCheckCircle, FiCircle, FiPlus, FiPrinter, FiSettings, FiTrash } from "react-icons/fi";
import { CircularProgress, IconButton, InputAdornment, TextField, useMediaQuery, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import Switch from '@mui/material/Switch';

import flex from "../../styles/flex";
import TabbedView from "../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../components/lists/TabbedView/TabbedViewPage";
import VehiclesAtLocation from "./components/VehiclesAtLocation";
import KeysAtLocation from "./components/KeysAtLocation";
import AssetMovementsAtLocation from "./components/AssetMovementsAtLocation";
import { isAdmin, isSysAdmin, locationPathTextToFriendly } from "../../utility/utils";
import { useHistory } from "react-router-dom";
import { useUserPermissions } from "@/hooks/useUserPermissions";

import Splitter from "../../components/splitter/Splitter";
import { SessionContext } from "../../utility/useSession";
import { storeValue, retrieveValue } from "../../utility/storage";

import { RootState, useAppDispatch } from "../../state/store";
import { setCondensedView } from "../../state/reducers/locationsReducer";

import { useLabelPrinter } from "../../components/labels";
import ErrorDialog from "../../components/notifications/errorDialog/ErrorDialog";
import { useLabelTemplates, printableLocationTypes } from "../../components/labels/labelTemplates";
import TreeView, { ITreeViewState, TreeViewCommand, TreeViewContext, ITreeViewRecord } from "../../components/treeView/TreeView";
import LocationsTreeViewLoader from "../../components/treeView/loaders/LocationsTreeViewLoader";
import { LocationsTreeViewDesign } from "../../components/treeView/designs/LocationsTreeViewDesign";
import AssetMovementsByUser from "./components/AssetMovementsByUser";
import { FaUndo } from "react-icons/fa";
import { ProgressDialog } from "../../components/notifications/progressDialog/progressDialog";
import KeyAuditsAtLocation from "./components/KeyAuditsAtLocation";
import KeyAuditsDueAtLocation from "./components/KeyAuditsDueAtLocation";
import LocationCreateDialog from "./LocationCreateDialog/LocationCreateDialog";
import LocationEditDialog from "./LocationEditDialog/LocationEditDialog";
import VehicleAuditsAtLocation from "./components/VehicleAuditsAtLocation";
import VehicleAuditsDueAtLocation from "./components/VehicleAuditsDueAtLocation";
import usePaths from "../../utility/paths";
import { useReleaseManagement, ReleaseFlag, useFeatureVariant } from "../../utility/feature-management";
import KeysHeld from "./components/KeysHeld";
import { useTranslation } from "react-i18next";
import LocationDeleteDialog from "./LocationDeleteDialog/LocationDeleteDialog";
import IssuesAtLocation from "./components/vehicleIssues/IssuesAtLocation";

const LocationsPage = () => {
  return <LocationsPageV2 />;
}

const LocationsPageV2 = () => {
  const paths = usePaths();
  const history = useHistory();
  const session = useContext(SessionContext);
  const isDeleteLocationFromHierarchyReleased = useReleaseManagement(ReleaseFlag.releaseDeleteLocationFromHierarchy);
  const isFilteredLocationSearchReleased = useReleaseManagement(ReleaseFlag.releaseLocationHierarchySearchFilter);
  const isLocationDragAndDropReleased = useReleaseManagement(ReleaseFlag.releaseLocationDragAndDrop);
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const isRbacLocationsManagementReleased = useReleaseManagement(ReleaseFlag.releaseRbacLocationsManagement);
  const { hasPermission } = useUserPermissions();

  const { t } = useTranslation();

  let historyPath = history.location.pathname.substring(paths.locations.length);

  let historySelectedId: number;
  if (historyPath.length == 0 || historyPath == "/") {
    historyPath = "/";
    historySelectedId = -1;
  } else {
    historySelectedId = parseInt(historyPath.split("/")[historyPath.split("/").length - 2]);
  }

  if (isNaN(historySelectedId)) {
    historySelectedId = -1;
  }

  const theme = useTheme();
  const isLargeDevice = useMediaQuery(theme.breakpoints.up('md'));
  const { labelPrinter } = useLabelPrinter();
  const { generateLocationPrintJob } = useLabelTemplates();
  const setLocationActive = api.hooks.assetLocation.setActive();
  const moveLocationAPI = api.hooks.assetLocation.move();
  const reorderLocationAPI = api.hooks.assetLocation.moveToPosition();
  const getSiblingsAPI = api.hooks.assetLocation.getByParent();

  const [displayInactiveLocations, setDisplayInactiveLocations] = useState(false);
  const [showCreateLocation, setShowCreateLocation] = useState(false);
  const [showEditLocation, setShowEditLocation] = useState(false);
  const [showDeleteLocation, setShowDeleteLocation] = useState(false);

  const [displayPercentage, setDisplayPercentage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [treeState, setTreeState] = useState<ITreeViewState>({
    treeRootId: 0,
    selectedRecord: historySelectedId == -1 ? null : { id: historySelectedId, path: historyPath } as AssetLocation,
    flags: {
      displayInactiveLocations,
      types: [

      ]
    }
  });

  useEffect(() => {
    if (treeState.selectedRecord != null) {
      if (historySelectedId != treeState.selectedRecord.id) {
        setTreeState({ ...treeState, selectedRecord: historySelectedId == -1 ? null : { id: historySelectedId, path: historyPath } as AssetLocation })
      }
    }
  }, [historySelectedId])

  useEffect(() => {
    const displayCapacityAsPercentage = (retrieveValue('displayCapacityAsPercentage') === "true");
    updateTreeStateDisplayPercentageFlag(displayCapacityAsPercentage);
    setDisplayPercentage(displayCapacityAsPercentage);
  }, [displayPercentage])

  const carParkId = session.current.site?.home?.id ?? null;
  const [currentCarParkId, setCurrentCarParkId] = useState(carParkId);

  const reloadTreeView = (clearSelection: boolean) => {
    setTreeState({
      ...treeState,
      selectedRecord: clearSelection ? null : treeState.selectedRecord,
      flags: {
        displayInactiveLocations,
        types: [

        ]
      }
    });
  }


  useEffect(() => {
    setCurrentCarParkId(currentCarParkId);
    reloadTreeView(true);
  }, [carParkId]);



  const selectedRecord = treeState.selectedRecord as AssetLocation;

  const locationPathText = !selectedRecord ? "" : selectedRecord.pathText;

  const keyLocationTypes = new Set([AssetLocationTypes.DynamicBox, AssetLocationTypes.KeyLocker, AssetLocationTypes.KeyLockerBox, AssetLocationTypes.Hook, AssetLocationTypes.KeyBox, AssetLocationTypes.Keyroom]);
  const vehicleLocationTypes = new Set([AssetLocationTypes.Lane, AssetLocationTypes.Zone]);
  const driverLocationTypes = new Set([AssetLocationTypes.User, AssetLocationTypes.Users]);

  const isKeyLocation = !selectedRecord ? false : (
    selectedRecord.keyType != KeyManagementTypes.inherit ||
    keyLocationTypes.has(selectedRecord.type)
  );
  const isVehicleLocation = !selectedRecord ? false : vehicleLocationTypes.has(selectedRecord.type);
  const isDriverLocation = !selectedRecord ? false : driverLocationTypes.has(selectedRecord.type);
  const isCarPark = !selectedRecord ? false : (selectedRecord.type == AssetLocationTypes.CarPark);


  const printLaneByParent = api.hooks.assetLocation.getByParent();
  const printGetParent = api.hooks.assetLocation.getById();

  const printLocation = () => {
    if (!selectedRecord) {
      return;
    }
    if (selectedRecord.type === AssetLocationTypes.Zone) {
      printZone(0);
      return;
    }

    if (selectedRecord.type == AssetLocationTypes.Lane) {
      if (selectedRecord.keyType == KeyManagementTypes.inherit) {
        //We must obtain the parent location to determine the key management type
        printGetParent.send(session.current.context, { id: selectedRecord.parentId }, (parentRecord) => {
          if (parentRecord == null) {
            alert(t("errors.parentLocation"));
            return;
          }

          labelPrinter.print(generateLocationPrintJob(selectedRecord, parentRecord));
        }
        );
      } else {
        labelPrinter.print(generateLocationPrintJob(selectedRecord));
      }
      return;
    }

    labelPrinter.print(generateLocationPrintJob(selectedRecord));
  }


  const printZone = (offset: number) => {

    printLaneByParent.send(session.current.context, {
      parentId: selectedRecord.id,
      paging: {
        offset: offset,
        limit: 20
      }
    }, (records: AssetLocation[] | null) => {
      if (records != null) {
        offset += records.length;
        if (selectedRecord.keyType == KeyManagementTypes.inherit) {
          records = records.filter((r) => r.keyType != KeyManagementTypes.inherit);
          if (records == null) {
            records = [];
          }
        }
        if (records.length == 0) {
          return;
        }

        const printArray = () => {
          if (records == null || records.length == 0) return;
          labelPrinter.print(generateLocationPrintJob(records[0]), function () {
            if (records == null || records.length == 0) return;
            records.splice(0, 1);
            if (records.length > 0) {
              printArray();
            } else {
              printZone(offset);
            }
          });
        };

        printArray();

      }
    });
  }

  const handlePercentageDisplaySwitch = (val: boolean) => {
    setDisplayPercentage(val);
    storeValue('displayCapacityAsPercentage', val.toString());
    updateTreeStateDisplayPercentageFlag(val);
  }

  const updateTreeStateDisplayPercentageFlag = (val: boolean) => {
    const flags = {
      ...treeState.flags,
      displayPercentage: val,
    };
    setTreeState({ ...treeState, flags: flags });
  }

  const addLocation = () => {
    setShowCreateLocation(true);
  }

  const editLocation = () => {
    setShowEditLocation(true);
  }

  const deleteLocation = () => {
    setShowDeleteLocation(true);
  }

  const setActive = async (active: boolean) => {
    if (selectedRecord == null) {
      return;
    }

    setLocationActive.send(session.current.context, {
      active: active,
      id: selectedRecord.id
    }, (result) => {
      if (result == null) {
        return;
      }

      const record = { ...selectedRecord, active: active };

      setTreeState({
        ...treeState,
        command: {
          command: active == false && !displayInactiveLocations ? TreeViewCommand.remove : TreeViewCommand.update,
          targetId: record.id,
          payLoad: record
        },
        selectedRecord: (active == true || displayInactiveLocations) ? record : null
      });
    });
  }

  // Drag and Drop handlers
  const handleDragReorder = (draggedRecord: ITreeViewRecord, targetRecord: ITreeViewRecord, position: 'above' | 'below') => {
    const draggedLocation = draggedRecord as AssetLocation;
    const targetLocation = targetRecord as AssetLocation;

    // Prevent dropping items between different type groups
    if (draggedLocation.type !== targetLocation.type) {
      return;
    }

    getSiblingsAPI.send(session.current.context, {
      parentId: targetLocation.parentId,
      includeInActive: displayInactiveLocations,
    }, (siblings) => {
      if (!siblings) {
        return;
      }

      // Group siblings by type to work within type groups
      const sameTypeSiblings = siblings.filter(s => s.type === draggedLocation.type);
      const draggedIndex = sameTypeSiblings.findIndex(s => s.id === draggedLocation.id);
      const targetIndex = sameTypeSiblings.findIndex(s => s.id === targetLocation.id);

      let newPosition: number;
      if (position === "above") {
        newPosition = targetIndex;
      } else {
        newPosition = targetIndex + 1;
      }

      // Adjust position if dragged item is being moved from before the target
      if (draggedIndex < targetIndex && position === "below") {
        newPosition = targetIndex; // No adjustment needed since we remove first
      } else if (draggedIndex < targetIndex && position === "above") {
        newPosition = targetIndex - 1;
      }

      const wouldBeNoOp = draggedIndex === newPosition;

      if (wouldBeNoOp) {
        return;
      }

      reorderLocationAPI.send(session.current.context, {
        locationId: draggedLocation.id,
        targetPosition: newPosition,
        includeInActive: displayInactiveLocations
      }, (result) => {
        if (result) {
          reloadTreeView(false);
          // Expand the parent node after reordering
          setTreeState(prev => ({
            ...prev,
            command: {
              command: TreeViewCommand.expanded,
              targetId: draggedLocation.parentId,
              payLoad: null
            }
          }));
        }
      });
    });
  };

  /*
      const moveDown = async () => {
          if (selectedRecord == null) {
              return;
          }
          setState({
              ...state,
              command: {
                  command: TreeViewCommand.moveDown,
                  targetId: selectedRecord.id,
              },
          });
      }
      const moveUp = async () => {
          if (selectedRecord == null) {
              return;
          }
          setState({
              ...state,
              command: {
                  command: TreeViewCommand.moveUp,
                  targetId: selectedRecord.id,
              },
          });
      }
  */
  const canDeleteLocation = selectedRecord != null
    && (isDriverLocation == false || selectedRecord.type == AssetLocationTypes.DynamicBox)
    && selectedRecord.type != AssetLocationTypes.CarPark
    && selectedRecord.type != AssetLocationTypes.Customer

  const canPermanentlyDeleteLocation = selectedRecord &&
    [
      AssetLocationTypes.Zone,
      AssetLocationTypes.Lane,
      AssetLocationTypes.Keyroom,
      AssetLocationTypes.Hook,
      AssetLocationTypes.KeyBox,
      AssetLocationTypes.DynamicBox
    ].includes(selectedRecord.type);

  const canAddLocation = selectedRecord != null && isDriverLocation == false

    && selectedRecord.type != AssetLocationTypes.Customer
    && selectedRecord.type != AssetLocationTypes.Lane
    && selectedRecord.type != AssetLocationTypes.Hook

    && selectedRecord.type != AssetLocationTypes.KeyLockerBox
    && selectedRecord.type != AssetLocationTypes.DynamicBox

  const editableLocation = selectedRecord != null && selectedRecord.type != AssetLocationTypes.User && selectedRecord.type != AssetLocationTypes.Users;

  const isPrintAllowed = () => {
    if (
      [AssetLocationTypes.User, AssetLocationTypes.Users].includes(selectedRecord.type)
      && session.current.site?.user.location.id !== selectedRecord.id
      && !isAdmin(session)
    ) {
      return false;
    }

    return true;
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // If the search term is empty, reload the tree view
    if (value.length <= 0) {
      reloadTreeView(true);
    }
  };

  const handleSearchSubmit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }

    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) {
      return;
    }

    // Determine if this is an exact match search (with quotes ' or " around the search term)
    const isExactMatch = /^["'](.*)["']$/.test(trimmedSearch);
    // If it is an exact match, remove the quotes
    const searchValue = isExactMatch ?
      trimmedSearch.slice(1, -1) :
      trimmedSearch;

    setTreeState({
      ...treeState,
      flags: {
        ...treeState.flags,
        displayInactiveLocations,
        displayPercentage,
        filter: searchValue,
        exactMatch: isExactMatch,
        types: []
      }
    });
  };

  return (
    <TreeViewContext.Provider value={{
      state: treeState,
      setState: (node) => {
        setTreeState(node);
        if (node.selectedRecord) {
          const selected = node.selectedRecord as AssetLocation;
          history.push(`${paths.locations}${selected.path}`);
        }
      },
      treeViewLoader: LocationsTreeViewLoader,
      getNodeDesign: LocationsTreeViewDesign,
      onDragReorder: isLocationDragAndDropReleased && hasPermission("assetTracking.location.reorder") ? handleDragReorder : undefined,
      enableDragAndDrop: isLocationDragAndDropReleased && hasPermission("assetTracking.location.reorder"),
    }}>
      <ErrorDialog err={labelPrinter.err}></ErrorDialog>
      <ErrorDialog err={setLocationActive.err}></ErrorDialog>
      <ErrorDialog err={moveLocationAPI.err}></ErrorDialog>
      <ErrorDialog err={reorderLocationAPI.err}></ErrorDialog>
      <ProgressDialog status={setLocationActive.status}></ProgressDialog>
      <ProgressDialog status={moveLocationAPI.status}></ProgressDialog>
      <ProgressDialog status={reorderLocationAPI.status}></ProgressDialog>

      <LocationCreateDialog parentLocation={selectedRecord} show={showCreateLocation} onClose={(command) => {
        setShowCreateLocation(false);
        if (command) {
          reloadTreeView(false);
        }
      }} ></LocationCreateDialog>

      {selectedRecord && (
        <LocationEditDialog location={selectedRecord} show={showEditLocation} onClose={(command) => {
          setShowEditLocation(false);
          if (command) {
            reloadTreeView(false);
          }
        }} ></LocationEditDialog>
      )}

      {selectedRecord && isDeleteLocationFromHierarchyReleased && (
        <LocationDeleteDialog location={selectedRecord} show={showDeleteLocation} onErrorClose={() => {
          setShowDeleteLocation(false);
          setTreeState({
            ...treeState,
            command: {
              command: TreeViewCommand.reload,
              targetId: null,
              payLoad: null,
            },
            selectedRecord: null,
          });
        }} onClose={(reload) => {
          setShowDeleteLocation(false);

          if (reload) {
            reloadTreeView(true);
          }
        }} ></LocationDeleteDialog>
      )}

      <DashPage absoluteChildren={
        <div style={isLargeDevice ? styles.treePanel : styles.treePanelSmallDevice}>
          <Fragment>
            <Splitter>
              <div style={styles.toolBar}>
                <div style={flex.flexRows}>
                  <div style={flex.flexFill}>
                    <div style={{ padding: 13, paddingLeft: 20, color: "gray" }}>
                      <span><b>{t("locations.locationManager")}</b></span>
                    </div>
                  </div>

                  {(selectedRecord && printableLocationTypes.indexOf(selectedRecord.type) !== -1) && (
                    <div style={flex.flexAuto}>
                      {isPrintAllowed() &&
                        <IconButton
                          data-testid="print-location-button"
                          title={t("locations.printLocationBarcode")}
                          disabled={labelPrinter.busy}
                          onClick={() => {
                            printLocation();
                          }}>
                          {labelPrinter.busy && (
                            <CircularProgress size={20}></CircularProgress>
                          )}
                          {labelPrinter.busy == false && (
                            <FiPrinter></FiPrinter>
                          )}

                        </IconButton>
                      }
                    </div>
                  )}

                  {(isRbacReleased && isRbacLocationsManagementReleased ? hasPermission("assetTracking.location.manage") : isAdmin(session)) && (<Fragment>
                    {
                      (canAddLocation && selectedRecord) && (
                        <div style={flex.flexAuto}>
                          <IconButton
                            data-testid="add-location-button"
                            onClick={() => {
                              addLocation();
                            }}
                            title={t("locations.addNewLocation")}
                          >
                            <FiPlus />
                          </IconButton>
                        </div>
                      )
                    }
                    {/*selectedRecord != null && (
                                          <Fragment>
                                              <div style={flex.flexAuto}>
                                                  <IconButton onClick={() => {
                                                      moveUp();
                                                  }}><FiChevronUp></FiChevronUp></IconButton>
                                              </div>
                                              <div style={flex.flexAuto}>
                                                  <IconButton onClick={() => {
                                                      moveDown();
                                                  }}><FiChevronDown></FiChevronDown></IconButton>
                                              </div>
                                          </Fragment>
                                              )*/}

                  </Fragment>)}
                  {/* isAdmin check to be removed  once isRbacLocationsManagementReleased is deprecated */}
                  {(isRbacLocationsManagementReleased || isAdmin(session)) && (<Fragment>
                    {(
                      <Fragment>
                        {(!isDeleteLocationFromHierarchyReleased && canDeleteLocation == true && selectedRecord != null && selectedRecord.active == true) && (
                          <div style={flex.flexAuto}>
                            <IconButton
                              data-testid="deactivate-location-button"
                              title={t("locations.setLocationInactive")}
                              disabled={!hasPermission("assetTracking.location.updateStatus")}
                              onClick={() => {
                                setActive(false);
                              }}
                            >
                              <FiTrash />
                            </IconButton>
                          </div>
                        )}
                        {(!isDeleteLocationFromHierarchyReleased && canDeleteLocation == true && selectedRecord != null && selectedRecord.active == false) && (
                          <div style={flex.flexAuto}>
                            <IconButton
                              data-testid="activate-location-button"
                              title={t("locations.setLocationActive")}
                              disabled={!hasPermission("assetTracking.location.updateStatus")}
                              onClick={() => {
                                setActive(true);
                              }}
                            >
                              <FaUndo />
                            </IconButton>
                          </div>
                        )}
                        {(isDeleteLocationFromHierarchyReleased && canPermanentlyDeleteLocation && selectedRecord != null && (isSysAdmin(session))) && (
                          <div style={flex.flexAuto}>
                            <IconButton
                              data-testid="delete-location-button"
                              title={t("locations.deleteLocation")}
                              onClick={() => {
                                deleteLocation();
                              }}
                            >
                              <FiTrash />
                            </IconButton>
                          </div>
                        )}

                        <div style={flex.flexAuto}>
                          {
                            displayInactiveLocations ? (
                              <IconButton
                                data-testid="exclude-inactive-locations-button"
                                title={t("locations.excludeInactiveLocations")}
                                onClick={() => {
                                  setDisplayInactiveLocations(false);

                                  setTreeState({
                                    ...treeState,
                                    flags: { ...treeState.flags, displayInactiveLocations: false },
                                    selectedRecord: selectedRecord == null ? null : (selectedRecord.active == true ? selectedRecord : null)
                                  });
                                }}
                              >
                                <FiCheckCircle />
                              </IconButton>
                            ) : (
                              <IconButton
                                data-testid="include-inactive-locations-button"
                                title={t("locations.includeInactiveLocations")}
                                onClick={() => {
                                  setDisplayInactiveLocations(true);
                                  setTreeState({
                                    ...treeState,
                                    flags: { ...treeState.flags, displayInactiveLocations: true },
                                    selectedRecord: selectedRecord == null ? null : (selectedRecord.active == true ? selectedRecord : null)
                                  });
                                }}
                              >
                                <FiCircle />
                              </IconButton>
                            )
                          }
                        </div>

                        {
                          selectedRecord != null && editableLocation == true && (
                            <div style={flex.flexAuto}>
                              <IconButton
                                data-testid="edit-location-button"
                                onClick={() => {
                                  editLocation();
                                }}
                                title={t("locations.configSelectedLocation")}
                              >
                                <FiSettings />
                              </IconButton>
                            </div>
                          )
                        }

                      </Fragment>
                    )}

                  </Fragment>)}


                </div>
              </div>
              {isFilteredLocationSearchReleased && (
                <div style={{ padding: '10px 30px 0 30px', display: 'flex', alignItems: 'center' }}>
                  <form onSubmit={handleSearchSubmit} style={{ display: 'flex', width: '100%' }}>
                    <TextField
                      data-testid="search-locations-input"
                      autoFocus={false}
                      label={t("locations.searchLocations")}
                      variant="outlined"
                      size="small"
                      type="search"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchSubmit();
                          return false;
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              data-testid="search-locations-button"
                              onClick={handleSearchSubmit}
                              aria-label={t("locations.searchLocations")}
                              edge="end"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </form>
                </div>
              )}

              {/* PERCENTAGE DISPLAY SWITCH */}
              {(<div style={{ float: "right", paddingRight: 5 }}>
                <div style={{ fontSize: 10, marginBottom: -5, paddingTop: 5 }}>{t("locations.displayAs%")}</div>
                <Switch data-testid="percentage-display-switch" checked={displayPercentage} value={displayPercentage} onChange={(e, val) => (handlePercentageDisplaySwitch(val))} />
              </div>)}
              <div style={{ padding: 50, paddingLeft: 0 }} onClick={() => {
                setTreeState({ ...treeState, selectedRecord: null });
              }}>
                <TreeView />
              </div>

            </Splitter>
          </Fragment>
        </div>
      }>

        <LocationDetails
          selectedRecord={selectedRecord}
          locationPathText={locationPathText}
          isDriverLocation={isDriverLocation}
          isKeyLocation={isKeyLocation}
          isVehicleLocation={isVehicleLocation}
          isCarPark={isCarPark}
        />

      </DashPage>
    </TreeViewContext.Provider>
  );
}

interface LocationDetailsProps {
  selectedRecord: AssetLocation;
  locationPathText: string;
  isDriverLocation: boolean;
  isKeyLocation: boolean;
  isVehicleLocation: boolean;
  isCarPark: boolean;
}

const LocationDetails: FC<LocationDetailsProps> = ({
  selectedRecord,
  locationPathText,
  isDriverLocation,
  isKeyLocation,
  isVehicleLocation,
  isCarPark,
}) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();

  const isAuditIssueManagementEnabled = useReleaseManagement(ReleaseFlag.releaseAuditIssueManagement);

  if (session.current.site == null) {
    return <Fragment></Fragment>;
  }
  /* if (session.current.site.preferences.enable_device_manager != true) {
     return <NotFoundPage title={"Not available"} message={"Feature not enabled"}></NotFoundPage>
   }*/

  const laneAuditEnabled = session.current.site.preferences.lane_audit_enabled == true;

  const appDispatch = useAppDispatch();

  const locationsState = useSelector((state: RootState) => state.locations);
  const { isCondensedView } = locationsState;

  const sortedTabs = [];

  if (isVehicleLocation || isCarPark) {
    sortedTabs[0] =
      <TabbedViewPage title={t("locations.vehicles")} key={"key-vehicle"}>
        <VehiclesAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    sortedTabs[2] =
      <TabbedViewPage title={t("locations.vehicleMovements")} key={"vehicle-movements"}>
        <AssetMovementsAtLocation path={selectedRecord.path} type={AssetType.vehicle} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    if (laneAuditEnabled) {
      sortedTabs[10] =
        <TabbedViewPage title={(t("locations.laneAuditsDue"))} key={"lane-audits-due"}>
          <VehicleAuditsDueAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
        </TabbedViewPage>

      sortedTabs[11] =
        <TabbedViewPage title={t("locations.laneAuditHistory")} key={"lane-audit-history"}>
          <VehicleAuditsAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
        </TabbedViewPage>
    }
    if (isAuditIssueManagementEnabled) {
      sortedTabs[12] =
        <TabbedViewPage title={t("locations.vehicleIssues.title")} key={"vehicle-issues"}>
          <IssuesAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
        </TabbedViewPage>
    }

  }

  if (isKeyLocation) {
    sortedTabs[1] =
      <TabbedViewPage title={t("locations.keys")} key={"keys"}>
        <KeysAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    sortedTabs[3] =
      <TabbedViewPage title={t("locations.keyMovements")} key={"key-movements"}>
        <AssetMovementsAtLocation path={selectedRecord.path} type={AssetType.vehicleKey} isCondensedView={isCondensedView} />
      </TabbedViewPage>
  }

  if (isDriverLocation) {
    sortedTabs[4] =
      <TabbedViewPage title={t("locations.keysHeld")} key={"driver-key-movements"}>
        <KeysHeld path={selectedRecord.path} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    sortedTabs[5] =
      <TabbedViewPage title={t("locations.keysHistory")} key={"driver-key-history"}>
        <AssetMovementsAtLocation path={selectedRecord.path} type={AssetType.vehicleKey} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    sortedTabs[6] =
      <TabbedViewPage title={t("locations.keyMovements")} key={"driver-vehicle-movements"}>
        <AssetMovementsByUser locationId={selectedRecord.id} type={AssetType.vehicleKey} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    sortedTabs[7] =
      <TabbedViewPage title={t("locations.vehicleMovements")} key={"veh-movements"}>
        <AssetMovementsByUser locationId={selectedRecord.id} type={AssetType.vehicle} isCondensedView={isCondensedView} />
      </TabbedViewPage>
  }

  if (isKeyLocation || isCarPark) {
    sortedTabs[8] =
      <TabbedViewPage title={t("locations.keyAuditsDue")} key={"key-audits-due"}>
        <KeyAuditsDueAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
      </TabbedViewPage>

    sortedTabs[9] =
      <TabbedViewPage title={t("locations.keyAuditHistory")} key={"key-audits-history"}>
        <KeyAuditsAtLocation path={selectedRecord.path} isCondensedView={isCondensedView} />
      </TabbedViewPage>
  }

  const tabs = sortedTabs.filter((t) => t);

  const innerClassName = isCondensedView
    ? classes.panelInnerCondensed
    : classes.panelInner;

  return (
    <div className={classes.panel}>
      <div className={innerClassName}>
        {selectedRecord && (
          <Fragment>
            <div style={{ textAlign: "right", marginRight: isCondensedView ? 20 : 0 }}>
              <small>{t("locations.condensedView")}</small>
              <Switch data-testid="condensed-view-switch" onClick={() => {
                appDispatch(setCondensedView(!isCondensedView));
              }} value={isCondensedView} defaultChecked={isCondensedView} />
            </div>

            <div style={{ textAlign: "center" }}>
              <h1 style={styles.title}>
                {locationPathTextToFriendly(locationPathText)}
              </h1>

            </div>

            <TabbedView>
              {tabs}
            </TabbedView>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default LocationsPage;
